import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import VueRecaptcha from 'vue-recaptcha';

import { getUserProfile } from '@monjin/data/service/user';
import { matchError } from '@monjin/data/util';

import { consumeParticipantCode, findInvitation, getParticipantUserId } from '@api/claimInvitation';

import WithRender from './ParticipantJoin.html';

const JOIN_FAILED_SESSION_KEY = 'joinFailedCount';
const PARTICIPANT_CODE_PATTERN = new RegExp('^\\d+$');
const LOGGING_OUT_WARNING = 'You are already logged in. You will be logged out.';
const HEADING = 'Use your code to quickly join the online interview';
const isParticipantCode = (code: string) => PARTICIPANT_CODE_PATTERN.test(code);

enum INVALID_JOIN_CODE_ERRORS {
    '',
    'Please enter code.',
    'Oops, you have entered invalid code.',
    'Looks like there is some problem occured while joining interview.<br/> Please contact administrator.'
}

/**
 * TODO:
 * Use this same page for both One-way and Two-way interviews.
 * i.e. Route all one-way traffic also through this.
 * Currently only users who are joining one-way interviewfrom website are redirected through this page.
 */
@WithRender
@Component({
    components: { VueRecaptcha }
})
export default class ParticipantJoin extends Vue {

    public participantCode: string = '';

    // 0 - No error, 1 - No code, 2 - Invalid code, 3 - Other error
    public errorCode: 0 | 1 | 2 | 3 = 0;

    public loggingOutWarning: string = '';
    public loading: boolean = false;
    public iAmNotRobot: boolean = false;
    public joinFailedCount: number = this.getJoinFailedCount();
    public heading = HEADING;

    public get siteKey(): string {
        // @ts-ignore
        return window.Monjin.captchaKey;
    }

    public get showCaptcha(): boolean {
        return this.joinFailedCount >= 2 && !!this.siteKey;
    }

    public get isValidCaptcha(): boolean {
        return this.showCaptcha ? this.iAmNotRobot : true;
    }

    public get disabled(): boolean {
        return this.loading || !this.isValidCaptcha;
    }

    public get invalidJoinCodeError(): string {
        return INVALID_JOIN_CODE_ERRORS[this.errorCode];
    }

    public created() {
        if (this.$route.params.participantCode) {
            this.participantCode = this.$route.params.participantCode;
            this.checkIfUserLoggedIn();
        }
    }

    public onConfirm() {
        this.loggingOutWarning = '';

        if (!this.participantCode) {
            this.errorCode = 1;
        } else if (this.participantCode.length !== 6) {
            this.errorCode = 2;
        } else {
            const code = this.participantCode;
            this.errorCode = 0;

            /**
             * Code can be of 2 types
             *     1. Participant Code :- Code to join two-way interview (only numeric code)
             *     2. Invitation Code :- Code to join one-way interview (Alpha numeric code)
             * Normally, by default user is reqirected to route '/public/invitations/ABCDEF' for one-way interview
             * but this is special case when user tries to do one-way interview from website, we are redirecting
             * them from this page.
             */
            const isVACode = isParticipantCode(code);

            if (isVACode) {
                consumeParticipantCode(code)
                .then((x) => {
                    sessionStorage.removeItem(JOIN_FAILED_SESSION_KEY);
                    window.location.href = `/conclave/${x}/activity`;
                })
                .catch(matchError({
                    // @ts-ignore
                    500: () => {
                        this.errorCode = 3;
                        this.setJoinFailedCount();
                    },
                    anyError: () => {
                        this.errorCode = 2;
                        this.setJoinFailedCount();
                    }
                }));
            } else {
                sessionStorage.removeItem(JOIN_FAILED_SESSION_KEY);
                window.location.href = `/public/invitations/${code}`;
            }

            // @ts-ignore
        }
    }

    public onPinChange(joinCode: string) {
        this.errorCode = 0;

        if (joinCode && joinCode.length === 6) {
            this.checkIfUserLoggedIn();
        }
    }

    public checkIfUserLoggedIn() {

        this.loading = true;

        // Check if the user already logged by using @me as a ping pong API
        getUserProfile()
            .then((x) => {
                const code = this.participantCode;

                if (isParticipantCode(code)) {
                    getParticipantUserId(this.participantCode).then((userId) => {
                        this.loggingOutWarning = x.userId !== userId ? LOGGING_OUT_WARNING : '';
                    });
                } else {
                    findInvitation(code).then((y) => {
                        this.loggingOutWarning = x.userId !== y.userId ? LOGGING_OUT_WARNING : '';
                    });
                }
            })
            .finally(() => this.loading = false);
    }

    public setiAmNotRobot(value: boolean) {
        this.iAmNotRobot = value;
    }

    public setJoinFailedCount() {
        sessionStorage.setItem(JOIN_FAILED_SESSION_KEY, (this.getJoinFailedCount() + 1).toString());
        this.joinFailedCount = this.getJoinFailedCount();
    }

    public getJoinFailedCount(): number {
        return parseInt(sessionStorage.getItem(JOIN_FAILED_SESSION_KEY) || '0', 10);
    }
}
